/**
 * What a drag on the chart means, kept apart from the drawing.
 *
 * This is the arithmetic — a pointer moved this far, at this many pixels per day, so
 * these are the new dates — and it is separated out because it is the part that is easy
 * to get subtly wrong and impossible to check by looking at a chart. Every rule here is
 * one somebody would complain about if it were absent.
 *
 * The drawing and the event plumbing live in Gantt.tsx. Nothing here touches the DOM.
 */

/**
 * 'progress' IS GONE, and the type is where it had to go from.
 *
 * Dragging the bar to set a percentage stopped existing when the stage became the single answer
 * to how far along a task is — but only the HANDLE was removed. The kind, the pointer maths, the
 * ghost readout and a mutation posting a field the API now refuses all stayed, unreachable, for
 * anybody reading this file to mistake for working code. Taking it off the union is what made
 * the compiler point at every one of them.
 */
export type Grab = 'move' | 'start' | 'end' | 'link'

export type Drag = {
  kind: Grab
  key: string
  /** Where the pointer went down, in client coordinates. */
  fromX: number
  fromY: number
  /** Where it is now, in chart coordinates, for drawing a link line. */
  x: number
  y: number
  /** Whole days moved, snapped. */
  days: number
  /** True once the pointer has travelled far enough to be a drag rather than a click. */
  live: boolean
}

/**
 * How far the pointer must travel before a press becomes a drag.
 *
 * Without it every click that wobbles by a pixel moves a task by a day, and a chart
 * where selecting a bar sometimes reschedules it is a chart people stop touching.
 */
export const SLOP = 4

const DAY = 86_400_000

/** A date as the API wants it, in local time — never toISOString, which is UTC and
 *  hands back yesterday for anybody east of Greenwich after four in the afternoon. */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const parseDay = (iso: string) => new Date(`${iso}T00:00:00`)
export const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY)
export const dayDiff = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY)

/** Whole days for a pixel distance, rounded to the nearest — not truncated. */
export function daysMoved(deltaX: number, dayWidth: number): number {
  return Math.round(deltaX / dayWidth)
}

/**
 * The dates a finished drag should be saved as, or null if it changes nothing.
 *
 * Resizing is clamped so a bar can never be dragged inside out: pulling the start past
 * the finish would otherwise write a task that ends before it begins, which the server
 * would either reject or, worse, silently swap.
 */
export function resultOf(
  drag: Drag,
  row: { start?: string; end?: string },
): { start: string; end: string } | null {
  if (!row.start || !row.end || !drag.live || drag.days === 0) return null
  const start = parseDay(row.start)
  const end = parseDay(row.end)

  if (drag.kind === 'move') {
    return { start: iso(addDays(start, drag.days)), end: iso(addDays(end, drag.days)) }
  }
  if (drag.kind === 'start') {
    const moved = addDays(start, drag.days)
    // At most the finish itself: a one-day task is the shortest thing a bar can be.
    const clamped = moved > end ? end : moved
    return dayDiff(start, clamped) === 0 ? null : { start: iso(clamped), end: row.end }
  }
  if (drag.kind === 'end') {
    const moved = addDays(end, drag.days)
    const clamped = moved < start ? start : moved
    return dayDiff(end, clamped) === 0 ? null : { start: row.start, end: iso(clamped) }
  }
  return null
}

/**
 * Where a row being dragged up or down the grid should land.
 *
 * Returns the index of the gap it is over — 0 is above the first row, `rows.length` is
 * below the last. The row being dragged is excluded from the measurement, because a row
 * cannot be dropped into the space it is already occupying and offering that as a
 * destination makes the indicator flicker as you pass your own position.
 */
export function dropGap(offsetY: number, rowHeight: number, count: number): number {
  return Math.max(0, Math.min(count, Math.round(offsetY / rowHeight)))
}

/**
 * What dropping into a gap means: which row it goes under, and at what indent.
 *
 * The indent is the part worth stating. Dropping between two rows of a phase makes the
 * dragged row part of that phase — that is what dragging means on a Gantt, and the
 * alternative is dragging a row and then pressing Tab to finish the job. Dropping
 * directly under a phase's own line puts it inside as the first child, because that gap
 * is visually inside the phase and anything else would surprise.
 */
export function landing<T extends { key: string; depth: number; has_children: boolean; parent_key: string | null }>(
  gap: number,
  rows: T[],
): { after: string | null; parent: string | null } {
  const above = gap > 0 ? rows[gap - 1] : null
  if (!above) return { after: null, parent: null }
  // Under a phase's own line: inside it.
  if (above.has_children) return { after: above.key, parent: above.key }
  return { after: above.key, parent: above.parent_key }
}
